import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Catalogs|Benefits|Associated Credentials|Form|';
const sharedProperties: Pick<LocDef, 'shared' | 'htmlAllowed' | 'feature' | 'portal'> = {
	shared: false,
	htmlAllowed: false,
	feature: HubFeature.Benefit,
	portal: 'SP',
};

type LocNameStart = 'SPBenefitsAssociatedCredentialsForm';
export type SPBenefitsAssociatedCredentialsFormLocNames =
	| `${LocNameStart}NotificationMinimumSelection`
	| `${LocNameStart}NotificationChangesSaved`
	| `${LocNameStart}LabelGroup`
	| `${LocNameStart}LabelFilterByGroup`
	| `${LocNameStart}LabelName`
	| `${LocNameStart}LabelNameAbbreviation`
	| `${LocNameStart}LabelStartDate`
	| `${LocNameStart}LabelEndDate`
	| `${LocNameStart}ErrorStartDateRequired`
	| `${LocNameStart}ErrorStartDateComparison`
	| `${LocNameStart}ErrorInvalidDate`
	| `${LocNameStart}TitleTable`;

export const spBenefitsAssociatedCredentialsFormLocs: LocDefs<SPBenefitsAssociatedCredentialsFormLocNames> =
	{
		SPBenefitsAssociatedCredentialsFormNotificationMinimumSelection: {
			defaultValue: 'At least 1 credential selection is required',
			description: 'Notification message when no credentials are selected for a benefit',
			displayPath: `${pathStart}Credential Section Required`,
			displayType: 'Notification',
			legacyName:
				'Benefit/Catalogs.Benefits.AssociatedCredentials.NotificationMessage.MinimumSelection',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormNotificationChangesSaved: {
			defaultValue: 'Changes Saved',
			description: 'Notification message when changes are saved',
			displayPath: `${pathStart}Changes Saved`,
			displayType: 'Notification',
			legacyName:
				'Benefit/Catalogs.Benefits.AssociatedCredentials.NotificationMessage.ChangesSaved',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormLabelGroup: {
			defaultValue: 'Group',
			description: 'Column heading: Group.',
			displayPath: `${pathStart}Group`,
			displayType: 'Label',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.ColumnHeading.Group',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormLabelFilterByGroup: {
			defaultValue: 'Filter by Group',
			description: 'Label for: Filter by Group.',
			displayPath: `${pathStart}Filter by Group`,
			displayType: 'Label',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.FieldLabel.FilterGroup',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormLabelName: {
			defaultValue: 'Name',
			description: 'Column heading: Name.',
			displayPath: `${pathStart}Name`,
			displayType: 'Label',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.ColumnHeading.Name',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormLabelNameAbbreviation: {
			defaultValue: 'Abbreviation',
			description: 'Column heading: Abbreviation.',
			displayPath: `${pathStart}Abbreviation`,
			displayType: 'Label',
			legacyName:
				'Benefit/Catalogs.Benefits.AssociatedCredentials.ColumnHeading.Abbreviation',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormLabelStartDate: {
			defaultValue: 'Start Date',
			description: 'Label for: Start Date.',
			displayPath: `${pathStart}Start Date`,
			displayType: 'Label',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.FieldLabel.StartDate',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormLabelEndDate: {
			defaultValue: 'End Date',
			description: 'Label for: End Date.',
			displayPath: `${pathStart}End Date`,
			displayType: 'Label',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.FieldLabel.EndDate',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormErrorStartDateRequired: {
			defaultValue: 'Start date is required',
			description: 'Error saying that the start date is required',
			displayPath: `${pathStart}Start Date Required`,
			displayType: 'Error',
			legacyName:
				'Benefit/Catalogs.Benefits.AssociatedCredentials.FieldError.StartDateRequired',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormErrorStartDateComparison: {
			defaultValue: 'The start date must be before the end date',
			description: 'Error saying that the start date must come first',
			displayPath: `${pathStart}Start Date First`,
			displayType: 'Error',
			legacyName:
				'Credential.Achievements/CatalogsAchievements.Details.FieldError.StartDateComparison',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormErrorInvalidDate: {
			defaultValue: 'Valid dates must be in the future',
			description: 'Error saying that the dates must be in the future',
			displayPath: `${pathStart}Invalid Date`,
			displayType: 'Error',
			legacyName: 'Benefit/Catalogs.Benefits.AssociatedCredentials.FieldError.InvalidDate',
			...sharedProperties,
		},
		SPBenefitsAssociatedCredentialsFormTitleTable: {
			defaultValue: 'Associated Credentials',
			description: 'Table Title',
			displayPath: `${pathStart}Table Title`,
			displayType: 'Title',
			...sharedProperties,
		},
	};

export type SPBenefitsAssociatedCredentialsFormLocType =
	LocMap<SPBenefitsAssociatedCredentialsFormLocNames>;
